import { useState } from 'react';
import { Eye, EyeOff, Check, MailWarning } from 'lucide-react';
import { credentialsSignIn, resendVerificationEmail } from '../../../lib/funnelAuthClient';
import { useAuthModal } from '../../../contexts/AuthModalContext';
import {
  fieldInputVariants,
  primaryButtonVariants,
  Spinner,
  ErrorBanner,
} from '../authFormPrimitives';

const RESEND_COOLDOWN_SECONDS = 30;

// Ported from roammyway_funnel's LoginFormPanel.tsx (password tab +
// unverified-email view), calling the funnel's credentials endpoint
// directly instead of next-auth/react.
export default function PasswordStep({ email, onBack, onForgotPassword }) {
  const { handleAuthenticated } = useAuthModal();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const [unverifiedResendToken, setUnverifiedResendToken] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState(null);
  const [resendCooldownRemaining, setResendCooldownRemaining] = useState(0);

  const startResendCooldown = () => {
    const startedAt = Date.now();
    setResendCooldownRemaining(RESEND_COOLDOWN_SECONDS);
    const interval = setInterval(() => {
      const remaining = Math.max(0, RESEND_COOLDOWN_SECONDS - Math.floor((Date.now() - startedAt) / 1000));
      setResendCooldownRemaining(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 1000);
  };

  const onResendVerificationEmail = async () => {
    if (!unverifiedResendToken || resendLoading || resendCooldownRemaining > 0) return;
    setResendLoading(true);
    setResendError(null);
    setResendSuccess(false);
    try {
      const { ok, status, data } = await resendVerificationEmail(unverifiedResendToken);
      if (ok) {
        setResendSuccess(true);
        startResendCooldown();
      } else if (status === 429) {
        setResendError(
          data.retryAfter
            ? `Too many requests. Try again in ${Math.ceil(data.retryAfter / 60)} minute(s).`
            : 'Too many requests. Please try again later.',
        );
      } else {
        setResendError(data.error || 'This link has expired. Please sign in again to request a new one.');
      }
    } catch {
      setResendError('Network error. Please check your internet connection and try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const backToSignIn = () => {
    setUnverifiedResendToken(null);
    setResendError(null);
    setResendSuccess(false);
    setError(null);
  };

  if (unverifiedResendToken) {
    return (
      <div className="animate-auth-surface py-2 text-center">
        <div className="mb-4 flex justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/10">
            <MailWarning className="h-7 w-7 text-amber-600" aria-hidden="true" strokeWidth={2.5} />
          </div>
        </div>
        <h2 className="font-brand text-xl font-bold text-brand-blue">Verify your email</h2>
        <p className="mt-1 font-brand text-sm text-brand-text-secondary">
          We've sent a verification link to your email address. Please verify your email before continuing.
        </p>

        {resendSuccess && (
          <div className="mt-4">
            <div className="flex items-center justify-center gap-2 rounded-xl border border-brand-success/30 bg-brand-success/10 px-4 py-2.5 font-brand text-sm text-brand-success">
              <Check className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              Verification email sent. Check your inbox.
            </div>
          </div>
        )}
        {!resendSuccess && resendError && (
          <div className="mt-4">
            <ErrorBanner message={resendError} />
          </div>
        )}

        <div className="mt-5 space-y-3">
          <button
            type="button"
            onClick={onResendVerificationEmail}
            disabled={resendLoading || resendCooldownRemaining > 0}
            className={primaryButtonVariants({ tone: 'idle' })}
          >
            {resendLoading ? (
              <span className="flex items-center justify-center">
                <Spinner className="-ml-1 mr-2 text-white" />
                Sending...
              </span>
            ) : resendCooldownRemaining > 0 ? (
              `Resend in ${resendCooldownRemaining}s`
            ) : (
              'Resend verification email'
            )}
          </button>
          <button
            type="button"
            onClick={backToSignIn}
            className="w-full font-brand text-sm font-semibold text-brand-text-secondary transition-colors hover:text-brand-blue focus:outline-none focus-visible:underline"
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!password.trim()) {
      setError('Password is required.');
      return;
    }

    setLoading(true);
    try {
      const { ok, error: signInError } = await credentialsSignIn({ email, password });

      if (!ok) {
        if (signInError && signInError.startsWith('EMAIL_VERIFICATION_REQUIRED:')) {
          setUnverifiedResendToken(signInError.slice('EMAIL_VERIFICATION_REQUIRED:'.length));
          return;
        }
        setError(
          signInError && signInError.includes('Too many')
            ? signInError
            : 'Invalid email or password. Please try again.',
        );
        return;
      }

      // credentialsSignIn() returning ok isn't proof of a real session by
      // itself -- handleAuthenticated() independently re-verifies before
      // this shows success and closes the modal.
      const { ok: authenticated } = await handleAuthenticated();
      if (!authenticated) {
        setError('Sign-in was not completed. Please try again.');
        return;
      }
      setSuccess(true);
    } catch {
      setError('Network error. Please check your internet connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <h1 className="text-left font-brand text-[26px] font-bold leading-[1.2] tracking-[-0.3px] text-brand-blue lg:text-center lg:text-[28px] lg:tracking-[-0.4px]">
        Welcome back
      </h1>

      {error && (
        <div className="mb-4 mt-4">
          <ErrorBanner message={error} />
        </div>
      )}

      <form onSubmit={onSubmit} className="mt-4 space-y-4" noValidate>
        <div className="space-y-1.5">
          <label className="font-brand text-[13px] font-semibold text-brand-blue">Email address</label>
          <input
            type="email"
            readOnly
            value={email}
            className={`bg-brand-grey text-brand-text-secondary ${fieldInputVariants({ invalid: false, filled: true })}`}
          />
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label htmlFor="romaya-auth-password" className="font-brand text-[13px] font-semibold text-brand-blue">
              Password
            </label>
            <button
              type="button"
              onClick={onForgotPassword}
              tabIndex={loading ? -1 : 0}
              className="font-brand text-sm text-brand-teal-strong transition-colors hover:text-brand-blue focus:outline-none focus-visible:underline"
            >
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <input
              id="romaya-auth-password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              required
              disabled={loading || success}
              aria-invalid={Boolean(error)}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (error) setError(null);
              }}
              placeholder="Enter your password"
              className={`pr-12 ${fieldInputVariants({ invalid: Boolean(error), filled: Boolean(password) })}`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-1 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-lg text-brand-text-secondary transition-colors hover:text-brand-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal-strong lg:h-10 lg:w-10"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="h-5 w-5" aria-hidden="true" /> : <Eye className="h-5 w-5" aria-hidden="true" />}
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading || success}
          className={primaryButtonVariants({ tone: success ? 'success' : 'idle' })}
        >
          {success ? (
            <span className="flex items-center justify-center gap-2">
              <Check className="h-4 w-4" aria-hidden="true" />
              Success
            </span>
          ) : loading ? (
            <span className="flex items-center justify-center">
              <Spinner className="-ml-1 mr-2 text-white" />
              Signing in...
            </span>
          ) : (
            'Sign in'
          )}
        </button>
      </form>

      {!success && (
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={onBack}
            className="font-brand text-sm font-semibold text-brand-teal-strong transition-colors hover:text-brand-blue focus:outline-none focus-visible:underline"
          >
            Use a different email
          </button>
        </div>
      )}
    </>
  );
}
